//Components
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import Module from '../../../../../components/layout/Module'
import DataGrid from '../../../../../components/table/DataGrid'
import Avatar from '../../../../../components/avatar/Avatar'
import FloatingDate from './FloatingDate/FloatingDate'
import EditMarking from './EditMarking'
import MessageBubble from '../../components/MessageBubble/MessageBubble'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import AlertSaveButton from '../../../../button/AlertSaveButton'
import SavingOverlay from './SavingOverlay'

//Other dependencies
import { useEffect, useCallback, useRef, useMemo, memo, useState } from 'react'
import { loadAssessmentMaking } from '../../../../../store/actions/dataEntryAssessmentMarkingActions'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { assessmentMarkingBulk } from '../../../../../services/data-entry/assessments.service'
import { alertReset, toggleAlertModule } from '../../../../../store/actions/alert.actions'
import {
  updateAssessmentMarkingRecord,
  resetHasChangedAssessmentMarking,
  toggleLoadingAssessmentMarking
} from '../../../../../store/actions/dataEntryAssessmentMarkingActions'
import { useCustomColumns } from '../../hooks/useCustomColumns'
import { createSelector } from 'reselect'
import globalConfig from '../../../../../config/globalConfig'
import { Divider } from '@mui/material'
import './AssessmentsMarking.css'
import Filter from './Filter/Button'
import RoundTabs from './RoundTabs'
import useUnsavedChangesGuard from '../../../../../hooks/useUnsavedChangesGuard'

const selectMarkingList = (state) => state.dataEntryAssessmentMarking?.marking?.list ?? []
const selectFirstMarkingItem = createSelector([selectMarkingList], (list) => list[0] ?? null)

//Memoized
const MemoizedEditMarking = memo(EditMarking)
const MemoizedMessageBubble = memo(MessageBubble)
const MemoizedFloatingDate = memo(FloatingDate)

const autoSaveTime = globalConfig?.dataEntry?.markingAutoSaveTime
const defaultColumns = [
  {
    label: 'Photo',
    key: 'avatar',
    align: 'center',
    hide: true,
    show: true,
    width: 60,
    minWidth: 60,
    maxWidth: 60,
    className: 'sticky sticky-col left-50px bg-white z-1 border-l-0 min-w-60px max-w-60px',
    hideSorting: true
  },
  {
    align: 'left',
    label: 'Code',
    key: 'code',
    hide: true,
    show: true,
    delete: true,
    sort: true,
    width: 100,
    minWidth: 100,
    maxWidth: 100,
    className: 'sticky-col left-110px bg-white z-1 min-w-100px max-w-100px',
    filter: 'text'
  },
  {
    align: 'left',
    label: 'First Name',
    key: 'firstName',
    hide: true,
    show: true,
    delete: true,
    sort: true,
    filter: 'text',
    width: 160,
    minWidth: 160,
    maxWidth: 160,
    className: 'sticky sticky-col left-210px bg-white z-1 min-w-160px max-w-160px'
  },
  {
    align: 'left',
    label: 'Last Name',
    key: 'lastName',
    hide: true,
    show: true,
    delete: true,
    sort: true,
    filter: 'text',
    width: 160,
    minWidth: 160,
    maxWidth: 160,
    className: 'sticky-col left-370px bg-white z-1 min-w-160px max-w-160px'
  },
  {
    align: 'left',
    label: 'Form',
    key: 'form',
    hide: true,
    show: true,
    delete: true,
    sort: true,
    filter: 'custom',
    width: 120,
    minWidth: 120,
    maxWidth: 120,
    className: 'sticky sticky-col left-530px bg-white z-1 min-w-120px max-w-120px'
  },
  {
    align: 'left',
    label: 'Year',
    key: 'yearLevel',
    hide: true,
    show: true,
    delete: true,
    sort: true,
    filter: 'custom',
    width: 80,
    minWidth: 80,
    maxWidth: 80,
    className: 'sticky sticky-col left-530px bg-white z-1 min-w-80px max-w-80px',
    sortKey: 'yearLevelSort'
  },
  {
    align: 'left',
    label: 'Date',
    key: 'date',
    hide: true,
    show: false,
    delete: true,
    filter: 'custom'
  }
]
const additionalColumns = [
  {
    align: 'center',
    label: 'Comments',
    key: 'comments',
    show: true,
    delete: true,
    filter: 'text',
    hideSorting: true
  }
]

const AssessmentsMarking = () => {
  const currentModule = 'dataentryassessment'
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  // Volver a la lista de origen: los profes entran desde /my-assessments y los
  // admin/maestro desde /assessments. Cada lista pasa `state.from`; fallback a la
  // lista admin para acceso directo/deep-link. Antes estaba hardcodeado a
  // /assessments, dejando al profe en una lista donde no puede marcar.
  const returnPath = location.state?.from || '/dashboard/data-entry/assessments'
  const saveRef = useRef(null)
  const { enqueueSnackbar } = useSnackbar()
  const { assessmentId } = useParams()
  const firstMarkingItem = useSelector(selectFirstMarkingItem)
  const reduxList = useSelector((state) => state.dataEntryAssessmentMarking?.marking?.list ?? [])
  const saveList = useSelector((state) => state?.dataEntryAssessmentMarking?.saveList ?? [])
  const customColumns = useCustomColumns(firstMarkingItem)
  const loading = useSelector((state) => state.dataEntryAssessmentMarking?.marking?.loading ?? false)
  const totalRecords = useSelector((state) => state.dataEntryAssessmentMarking?.marking?.totalRecords ?? 0)
  const permissions = useSelector((state) => state?.session?.permissions?.dataentrymyassessments ?? {})
  const skip = useSelector((state) => state?.datatable?.assessmentMarking?.skip ?? 0)
  const rowsPerPage = useSelector((state) => state?.datatable?.assessmentMarking?.rowsPerPage ?? 100)
  const alertSave = useSelector((state) => state?.alert?.save || false)
  const status = firstMarkingItem ? firstMarkingItem.status || 'Closed' : 'Closed'
  const state = useSelector((state) => state?.filter?.assessmentMarking)
  const [isSaving, setIsSaving] = useState(false)
  // MF-399 — round seleccionado: si está finalizado, la grilla es de solo lectura.
  const rounds = useSelector((state) => state?.dataEntryAssessmentMarking?.rounds ?? [])
  const selectedRoundId = useSelector((state) => state?.dataEntryAssessmentMarking?.selectedRoundId ?? null)
  const selectedRoundFinalized = useMemo(
    () => rounds.find((r) => String(r._id) === String(selectedRoundId))?.status === 'finalized',
    [rounds, selectedRoundId]
  )

  // Guard against losing unsaved changes on browser navigation (back/forward/swipe)
  useUnsavedChangesGuard({
    hasChanges: saveList.length > 0,
    onSave: () => bulkMarkingSubmit(false),
    module: 'marking',
    mode: 'editing'
  })

  const onAlert = (show, value, onSave, onDontSave) => {
    dispatch(toggleAlertModule(show, 'marking', value, onSave, onDontSave))
  }

  const onChangeAlert = useCallback(() => {
    if (alertSave && !alertSave.mode) {
      onAlert(false, 'adding', () => {
        bulkMarkingSubmit(true)
      })
    }
  }, [onAlert, alertSave])

  const bulkMarkingSubmit = async (navigateToList = false) => {
    try {
      setIsSaving(true)
      const recordsToUpdate = saveList

      if (recordsToUpdate && recordsToUpdate?.length) {
        const { data, error } = await assessmentMarkingBulk({
          assessmentId,
          records: recordsToUpdate
        })

        if (error || !data) {
          enqueueSnackbar('We could not save your changes, please try again', {
            variant: 'error'
          })
        } else {
          dispatch(resetHasChangedAssessmentMarking())
          dispatch(alertReset())

          if (navigateToList) {
            navigate(returnPath)
          }
        }
      } else {
        if (navigateToList) {
          dispatch(resetHasChangedAssessmentMarking())
          dispatch(alertReset())
          navigate(returnPath)
        }
      }
    } catch (error) {
      enqueueSnackbar('We could not save your changes, please try again', {
        variant: 'error'
      })
    } finally {
      setIsSaving(false)
    }
  }

  const debounce = (func, delay) => {
    let timer
    return function (...args) {
      const context = this
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => func.apply(context, args), delay)
    }
  }

  const autoSaveMarking = useCallback(
    debounce(async () => {
      const recordsToUpdate = saveList
      if (recordsToUpdate.length) {
        try {
          setIsSaving(true)
          await assessmentMarkingBulk({ records: recordsToUpdate, assessmentId })
          dispatch(resetHasChangedAssessmentMarking())
          dispatch(alertReset())
        } catch (_error) {
          // Auto-save errors are silently ignored
        } finally {
          setIsSaving(false)
        }
      }
    }, 1000),
    [saveList]
  )

  const getCellsByStudent = useCallback(
    (record) => {
      if (!record) return {}

      const cells = {}
      record.questions.forEach((item) => {
        const { title, questionId } = item
        cells[title] = (
          <MemoizedEditMarking
            id={`question-marking-${questionId}`}
            question={item}
            record={record}
            onChangeAlert={onChangeAlert}
            assessmentId={assessmentId}
            disabled={status === 'Closed' || selectedRoundFinalized}
          />
        )
      })
      return cells
    },
    [onChangeAlert, selectedRoundFinalized]
  )

  const getCellsByStudents = useCallback((list = []) => list.map(getCellsByStudent), [getCellsByStudent])

  const onSaveCustomColumn = useCallback(async (payload = {}) => {
    const { name, value, recordId } = payload
    dispatch(
      updateAssessmentMarkingRecord({
        _id: recordId,
        [name]: value
      })
    )
    onChangeAlert()
  }, [])

  const generatedCells = useMemo(
    () => getCellsByStudents(reduxList),
    [reduxList, skip, rowsPerPage, selectedRoundFinalized]
  )

  const rows = useMemo(
    () =>
      reduxList.map((row, index) => ({
        ...row,
        index: index + 1 + rowsPerPage * skip,
        avatar: <Avatar size='small' variant='circular' alt={row?.code} src={`photos/${row?.photo}`} />,
        code: row.code ?? '',
        firstName: row?.userInfo?.firstName,
        lastName: row?.userInfo?.lastName,
        form: row?.userInfo?.data?.form || '',
        yearLevel: (
          <div className='flex flex-wrap items-center gap-1'>
            {row?.yearlevel?.map((yearLevel) => (
              <Chip
                key={`user-${row._id}-yearLevel-${yearLevel._id}`}
                size='small'
                label={yearLevel.name}
                variant='outlined'
              />
            ))}
          </div>
        ),
        yearLevelSort: row?.yearlevel?.map((yl) => yl?.name).join(', ') || '', // Raw string for sorting
        date: (
          <MemoizedFloatingDate
            value={row?.date ?? null}
            onSave={onSaveCustomColumn}
            name='date'
            recordId={row?._id}
            disabled={status === 'Closed'}
          />
        ),
        comments: (
          <MemoizedMessageBubble
            value={row?.comments || ''}
            title={'Comments'}
            onSave={onSaveCustomColumn}
            name='comments'
            recordId={row?._id}
            disabled={status === 'Closed'}
          />
        ),
        ...generatedCells[index]
      })),
    [generatedCells]
  )

  //Effect to set cells values by record
  useEffect(() => {
    dispatch(toggleLoadingAssessmentMarking())
  }, [generatedCells])

  //Effect to load data
  useEffect(() => {
    const filterData = {
      yearLevelId: state?.filter?.yearLevelId || [],
      classesCode: state?.filter?.classesCode || [],
      studentCode: state?.filter?.studentCode || '',
      studentFirstName: state?.filter?.studentFirstName || '',
      studentLastName: state?.filter?.studentLastName || '',
      studentHouse: state?.filter?.studentHouse || '',
      studentForm: state?.filter?.studentForm || ''
    }
    dispatch(loadAssessmentMaking(assessmentId, skip * rowsPerPage, rowsPerPage, filterData))
  }, [rowsPerPage, skip, assessmentId, state?.filter])

  // UseEffect for setting up the autosave interval
  useEffect(() => {
    const intervalId = setInterval(() => {
      autoSaveMarking()
    }, autoSaveTime)

    return () => clearInterval(intervalId)
  }, [autoSaveMarking])

  const tableColumns = useMemo(
    () => [
      {
        label: '#',
        key: 'index',
        align: 'center',
        width: 50,
        show: true,
        hide: false,
        className: 'sticky-col left-0px bg-white z-1 border-r-0 min-w-50px max-w-50px'
      },
      ...defaultColumns.map((col) => ({ ...col, width: col.width || 100 })),
      ...customColumns,
      ...additionalColumns
    ],
    [customColumns]
  )

  return (
    <Module>
      <Module.Header
        moduleName={currentModule}
        title={
          firstMarkingItem ? (
            <div className='flex items-center gap-2'>
              {firstMarkingItem ? firstMarkingItem?.assessment?.name : ''}
              <Divider orientation='vertical' flexItem className=' bg-white' />
              <Chip label={status} size='small' className='ml-2' color='secondary' />
            </div>
          ) : (
            'Assessment Marking'
          )
        }
        titleLink={`/dashboard/data-entry/assessment-marking/${assessmentId}`}
      />
      <Module.BodyHeader className='border-b border-b-2 justify-between border-primary '>
        <div className='flex items-center gap-3 flex-wrap'>
          <Filter />
          <RoundTabs canFinalize={permissions?.marking && status !== 'Closed'} />
        </div>
        <div className='flex items-center gap-2'>
          <AlertSaveButton
            submodule='marking assessment'
            disabled={loading || isSaving}
            icon={['fal', 'times']}
            title='Cancel'
            color='primary'
            variant='outlined'
            className='shadow-sm'
            onDontSave={() => navigate(returnPath)}
          />
          {permissions?.marking && (
            <>
              <ButtonStartIcon
                size='small'
                onClick={() => bulkMarkingSubmit(true)}
                startIcon={'circle-check'}
                ref={saveRef}
                disabled={status === 'Closed' || loading || isSaving}
              >
                Finish
              </ButtonStartIcon>
            </>
          )}
        </div>
      </Module.BodyHeader>
      <Module.BodyContainer>
        {/* Saving overlay */}
        <SavingOverlay isVisible={isSaving} />
        <Grid item xs={12}>
          <DataGrid
            id='table-assessment-marking'
            rows={rows}
            loading={loading}
            labelNoRows='No students found for this assessment'
            nameModule='assessmentMarking'
            headers={[...defaultColumns]}
            totalRows={totalRecords}
            skip={skip}
            rowsPerPage={rowsPerPage}
            columns={tableColumns}
          />
        </Grid>
      </Module.BodyContainer>
    </Module>
  )
}
export default AssessmentsMarking
